import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Slider,
  Tooltip,
  Chip,
  Paper,
  LinearProgress,
  Fade,
  Zoom,
  useTheme,
  alpha,
  Breadcrumbs,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  SwapHoriz as SwapHorizIcon,
  VolumeUp,
  Pause,
  PlayArrow,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Circle,
  AutoFixHigh,
  Translate as TranslateIcon,
  Language as LanguageIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Speed as SpeedIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { ContentState, Editor, EditorState, RichUtils } from "draft-js";
import {
  checkTranslator,
  checkTranslatorLang,
} from "../../services/translatorServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { addUserLogs } from "../../services/userLogServices";
import { useCheckFeatureAccess } from "../CheckFeature";
import SubscriptionRequired from "../SubscriptionRequired";
import { useCheckSubscriptionPlanAccess } from "../SubscriptionAccess";
import { checkAIRequestLimit } from "../aiRequestLimit";
import { useHistory } from "react-router-dom";
import jwtDecode from "jwt-decode";
import Poetlogopen from "../../static/POET_Logo_Pen.png";
import { withStyles } from "@material-ui/core/styles";

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: "blue",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const SelectDropdownMui = ({
  optionsList,
  selectLabel,
  onChange,
  value,
  error,
  ...props
}) => (
  <select
    onChange={onChange}
    value={value}
    style={{
      width: "100%",
      padding: "12px",
      borderRadius: "12px",
      border: error ? "2px solid #f44336" : "2px solid #e3f2fd",
      fontSize: "14px",
      backgroundColor: "white",
      cursor: "pointer",
      transition: "border 0.3s ease",
      boxShadow: error ? "0 0 0 2px rgba(244, 67, 54, 0.2)" : "none",
    }}
    {...props}
  >
    <option value="">{selectLabel}</option>
    {optionsList.map((item, idx) => (
      <option key={idx} value={item.value}>
        {item.name}
      </option>
    ))}
  </select>
);

const SelectDropdownSearch = ({
  optionsList,
  selectLabel,
  onChange,
  value,
  isDarkMode,
  error,
  inputRef,
  ...props
}) => {
  const borderColor = error ? "#f44336" : "#e3f2fd";
  const backgroundColor = isDarkMode ? "#2A2A2A" : "white";

  return (
    <select
      ref={inputRef}
      onChange={(e) => onChange(e, e.target.value)}
      value={value}
      style={{
        width: "100%",
        padding: "12px",
        borderRadius: "12px",
        border: `2px solid ${borderColor}`,
        fontSize: "14px",
        backgroundColor: backgroundColor,
        color: isDarkMode ? "white" : "black",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: error ? "0 0 0 2px rgba(244, 67, 54, 0.2)" : "none",
        outline: "none",
      }}
      {...props}
    >
      <option value="">{selectLabel}</option>
      {optionsList.map((lang, idx) => (
        <option key={idx} value={lang}>
          {lang}
        </option>
      ))}
    </select>
  );
};

const Loader = ({ loading }) => (loading ? <LinearProgress /> : null);

const ModernTranslator = () => {
  const theme = useTheme();
  const userDetails = useSelector((user) => user);
  // const userID = userDetails.user.currentUser._id;
  const token = sessionStorage.getItem("token");

  const getIdentityFromToken = () => {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");

    if (!token) return null;

    try {
      const decoded = jwtDecode(token);

      if (decoded.role === "guest") {
        return { machineId: decoded.machineId };
      } else {
        return { userId: decoded._id };
      }
    } catch (err) {
      console.error("Invalid token");
      return null;
    }
  };

  const identity = getIdentityFromToken();

  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [detectedLanguage, setDetectedLanguage] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [secondaryeditorState, setSecondaryEditorState] = useState(
    EditorState.createEmpty(),
  );
  const [wordsCount, setWordsCount] = useState(0);
  const [opWordsCount, setOpWordsCount] = useState(0);
  const [wordCheck, setWordCheck] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasText, setHasText] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedProfession, setSelectedProfession] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPauseResumeToggle, setShowPauseResumeToggle] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const [isSliderMoving, setIsSliderMoving] = useState(false);
  const [speakLanguage, setSpeakLanguage] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [languageError, setLanguageError] = useState(false);
  const [hasTranslatedOnce, setHasTranslatedOnce] = useState(false);
  const [shouldFocusLanguage, setShouldFocusLanguage] = useState(false);

  // State for word limit warning dialog
  const [wordLimitDialogOpen, setWordLimitDialogOpen] = useState(false);
  const [wordLimitExceeded, setWordLimitExceeded] = useState(false);

  const accessToTranslate = useCheckFeatureAccess("translator", "translator");
  const accessToTranslatorScreen = useCheckSubscriptionPlanAccess("Translator");
  const accessToSpeaker = useCheckSubscriptionPlanAccess("speaker");
  const [subReqOpen, setSubReqOpen] = useState(false);
  const [isFeatureInside, setIsFeatureInside] = useState(false);
  const history = useHistory();

  // Refs
  const prevSelectedLanguageRef = useRef("");
  const prevEditorContentRef = useRef("");
  const languageSelectRef = useRef(null);

  const languageList = [
    "Arabic",
    "Assamese",
    "Bengali",
    "Cebuano",
    "Chinese",
    "Czech",
    "Danish",
    "Dutch",
    "English",
    "Finnish",
    "French",
    "German",
    "Greek",
    "Gujarati",
    "Hebrew",
    "Hindi",
    "Hungarian",
    "Indonesian",
    "Italian",
    "Japanese",
    "Kannada",
    "Korean",
    "Malay",
    "Malayalam",
    "Marathi",
    "Nepali",
    "Norwegian",
    "Odia",
    "Persian",
    "Polish",
    "Portuguese",
    "Punjabi",
    "Romanian",
    "Russian",
    "Sanskrit",
    "Spanish",
    "Swedish",
    "Tagalog",
    "Tamil",
    "Telugu",
    "Thai",
    "Turkish",
    "Urdu",
    "Ukrainian",
    "Vietnamese",
  ];

  const industryList = [
    { value: "IT", name: "IT" },
    { value: "Healthcare", name: "Healthcare" },
    { value: "Finance", name: "Finance" },
    { value: "Agriculture", name: "Agriculture" },
    { value: "Education", name: "Education" },
    { value: "Retail", name: "Retail" },
    { value: "Telecommunication", name: "Telecommunication" },
  ];

  const professionList = [
    { value: "Content Writer", name: "Content Writer" },
    { value: "Business Owner", name: "Business Owner" },
    { value: "HR Professional", name: "HR Professional" },
    { value: "Marketing", name: "Marketing" },
    { value: "Product Manager", name: "Product Manager" },
    { value: "Medical Professional", name: "Medical Professional" },
    { value: "Legal Professional", name: "Legal Professional" },
  ];

  //full screen layout
  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.height = "100vh";
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100vh";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.margin = "";
      document.body.style.padding = "";
      document.body.style.height = "";
      document.body.style.overflow = "";
      document.documentElement.style.height = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (accessToTranslatorScreen == false) {
      setSubReqOpen(true);
    }
  }, [accessToTranslatorScreen]);

  useEffect(() => {
    const updateTheme = () => {
      const theme = localStorage.getItem("ThemeSwitch");
      setIsDarkMode(theme === "true");
    };
    updateTheme();
    window.addEventListener("storage", updateTheme);
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === "ThemeSwitch") {
        updateTheme();
      }
    };
    return () => {
      window.removeEventListener("storage", updateTheme);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (isPlaying) {
        window.speechSynthesis.cancel();
        setIsPlaying(false);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    console.log("Detector_", inputText, wordCheck);
    if (inputText !== "" && wordCheck !== "" && accessToTranslate) {
      handleDetect();
    }
  }, [wordCheck]);

  // Effect to focus language dropdown when error occurs
  useEffect(() => {
    if (shouldFocusLanguage && languageSelectRef.current) {
      languageSelectRef.current.focus();
      setShouldFocusLanguage(false);

      const languageSection = document.getElementById("translate-to-section");
      if (languageSection) {
        languageSection.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [shouldFocusLanguage]);

  // Effect to auto-translate when language changes after first translation
  useEffect(() => {
    if (
      hasTranslatedOnce &&
      selectedLanguage &&
      selectedLanguage !== prevSelectedLanguageRef.current &&
      inputText &&
      !loading
    ) {
      console.log("Auto-translating due to language change...");
      handleTranslate(false);
    }

    prevSelectedLanguageRef.current = selectedLanguage;
  }, [selectedLanguage, inputText]);

  // Effect to auto-translate when source text changes after first translation
  useEffect(() => {
    const currentContent = editorState.getCurrentContent().getPlainText();

    if (
      hasTranslatedOnce &&
      selectedLanguage &&
      currentContent &&
      currentContent !== prevEditorContentRef.current &&
      !loading
    ) {
      console.log("Auto-translating due to source text change...");
      handleTranslate(false);
    }

    prevEditorContentRef.current = currentContent;
  }, [editorState]);

  const handleListen = () => {
    if (accessToSpeaker == false) {
      setSubReqOpen(true);
      setIsFeatureInside(true);
      return;
    }

    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const textToSpeak = secondaryeditorState
        .getCurrentContent()
        .getPlainText();
      const utterance = new SpeechSynthesisUtterance(textToSpeak);

      const voices = window.speechSynthesis.getVoices();
      console.log("Available Voices:", voices);

      const femaleVoices = voices.filter(
        (voice) =>
          voice.name.includes("Microsoft Zira - English (United States)") &&
          voice.lang.includes(speakLanguage.toLowerCase()),
      );

      console.log("femaleVoices:", femaleVoices);

      if (femaleVoices.length > 0) {
        utterance.voice = femaleVoices[0];
      } else {
        console.warn("Female voice not found");
      }

      utterance.lang = speakLanguage;
      utterance.rate = currentSpeed;

      utterance.onend = () => {
        console.log("Speech synthesis completed");
        setIsPlaying(false);
        setShowPauseResumeToggle(false);
      };

      if (!isPlaying) {
        window.speechSynthesis.speak(utterance);
        setIsPlaying(true);
        setShowPauseResumeToggle(true);
      } else {
        window.speechSynthesis.pause();
        setIsPlaying(false);
        setShowPauseResumeToggle(false);
      }
    } else {
      console.error("Speech synthesis not supported in this browser.");
    }
  };

  const handleSpeedChange = (_, newValue) => {
    if (!isSliderMoving) {
      setSpeed(newValue);
      setCurrentSpeed(newValue);

      if (isPlaying) {
        const textToSpeak = secondaryeditorState
          .getCurrentContent()
          .getPlainText();
        const utterance = new SpeechSynthesisUtterance(textToSpeak);

        const voices = window.speechSynthesis.getVoices();

        const femaleVoices = voices.filter(
          (voice) =>
            voice.name.includes("Microsoft Zira - English (United States)") &&
            voice.lang.includes(speakLanguage.toLowerCase()),
        );

        if (femaleVoices.length > 0) {
          utterance.voice = femaleVoices[0];
        } else {
          console.warn("Female voice not found. Using default voice.");
        }

        utterance.lang = speakLanguage;
        utterance.rate = newValue;

        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      window.speechSynthesis.pause();
    } else {
      window.speechSynthesis.resume();
    }
    setIsPlaying(!isPlaying);
  };

  const clearListenFunctionality = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setShowPauseResumeToggle(false);
  };

  const handleOuputChange = (newEditorState) => {
    const contentState = newEditorState.getCurrentContent();
    setSecondaryEditorState(newEditorState);

    if (contentState.hasText() === false) {
      clearListenFunctionality();
    }
  };

  const handleDetect = async () => {
    try {
      if (inputText === "") {
        return;
      }
      let text = inputText.split(" ")[0];
      console.log("texttttttttttttt", inputText);
      const response = await checkTranslatorLang(text);
      const detectedLanguage = response.data[0];
      console.log("detectedLanguage:", detectedLanguage);
      setDetectedLanguage(detectedLanguage);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
    }
  };

  // Updated handleTranslate with word limit check
  const handleTranslate = async (isManual = true) => {
    // Reset error state
    setLanguageError(false);

    // Validate language selection FIRST
    if (!selectedLanguage) {
      if (isManual) {
        setLanguageError(true);
        toast.warn("Please select a language to translate to");
        setShouldFocusLanguage(true);
      }
      setLoading(false);
      return;
    }

    // Check word limit
    const wordCount = editorState
      .getCurrentContent()
      .getPlainText()
      .trim()
      .split(/\s+/).length;

    if (wordCount > 1500) {
      setWordLimitDialogOpen(true);
      setWordLimitExceeded(true);
      return;
    }

    // const checkailimit = await checkAIRequestLimit(identity);
    // console.log(checkailimit, "_checkailimit");
    // if (checkailimit == false) {
    //   toast.warn("AI request limit reached.");
    //   return;
    // }

    setLoading(true);
    try {
      let input;
      const selection = editorState.getSelection();
      input = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getAnchorKey())
        .getText()
        .slice(selection.getStartOffset(), selection.getEndOffset());
      if (!input) {
        input = editorState.getCurrentContent().getPlainText();
      }

      if (input === "") {
        if (isManual) {
          toast.warn("Please enter text to translate");
        }
        setLoading(false);
        return;
      }

      console.log(
        "SelectedLang_",
        selectedLanguage,
        selectedIndustry,
        selectedProfession,
      );

      let data;
      if (selectedProfession !== "" && selectedIndustry !== "") {
        data = {
          selectedLanguage,
          selectedIndustry,
          selectedProfession,
          input,
          // userId: userID,
          ...identity,
        };
      } else if (selectedProfession !== "") {
        data = {
          selectedLanguage,
          selectedProfession,
          input,
          ...identity,
          // userId: userID,
        };
      } else if (selectedIndustry !== "") {
        data = {
          selectedLanguage,
          selectedIndustry,
          input,
          ...identity,
          // userId: userID,
        };
      } else {
        data = {
          selectedLanguage,
          input,
          // userId: userID,
          isCoAuthor: false,
          ...identity,
        };
      }

      const response = await checkTranslator(data);
      const translatedText = response?.data?.output;
      console.log("translatedText", translatedText);

      if (response.status == 400) {
        toast.warn(response.message);
        return;
      }

      const data1 = {
        // userId: sessionStorage.getItem("userId"),
        // organizationId: sessionStorage.getItem("organizationId"),
        actionType: "translator",
        ...identity,
      };

      const addLogs = await addUserLogs(data1);
      if (addLogs.status !== 200) {
        toast.error(addLogs.message);
      }

      let textWithoutLeadingNewlines = translatedText[0].replace(/^\n+/, "");
      let wordLength = translatedText[0].split(/[ -]+/);

      setOpWordsCount(wordLength.length);
      const contentState = ContentState.createFromText(
        textWithoutLeadingNewlines,
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setTranslatedText(translatedText);
      setSecondaryEditorState(newEditorState);

      if (!hasTranslatedOnce) {
        setHasTranslatedOnce(true);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
      toast.warn(error.message);
      setLoading(false);
    }
  };

  // Handle word limit dialog close
  const handleWordLimitDialogClose = () => {
    setWordLimitDialogOpen(false);
    setWordLimitExceeded(false);
  };

  const handleBreadcrumbClick = (event, path) => {
    event.preventDefault();
    window.location.href = "/docTags";
    // history.push(path);
  };

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);

    const plainTextContent = newEditorState.getCurrentContent().getPlainText();
    setInputText(plainTextContent);

    if (plainTextContent === "") {
      setDetectedLanguage("");
      setSelectedIndustry("");
      setSelectedProfession("");
      setWordsCount(0);
      setOpWordsCount(0);
      setHasText(false);
    } else {
      setHasText(true);
    }

    if (plainTextContent !== "") {
      const trimmedText = plainTextContent.trim();
      const words = trimmedText
        .split(/\s+/)
        .filter((word) => word !== "").length;
      let splitinput = plainTextContent.split(" ");
      if (splitinput.length > 1) {
        setWordCheck(splitinput[0]);
      }
      console.log("words_", words);
      setWordsCount(words);
    }
  };

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    console.log("editorstate_", newState);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const handlecopy = () => {
    let text = secondaryeditorState.getCurrentContent().getPlainText();
    if (text == "") {
      toast.warn(`You don't have text, to copy`);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function () {
        console.log("Async: Copying to clipboard was successful!");
        if (text) toast.info("Your text Copied!");
      },
      function (err) {
        console.error("Async: Could not copy text: ", err);
      },
    );
  };

  const handleDownload = () => {
    if (secondaryeditorState.getCurrentContent().getPlainText() == "") {
      toast.warn(`You don't have text, to download`);
      return;
    }
    const blob = new Blob(
      [secondaryeditorState.getCurrentContent().getPlainText()],
      { type: "text/plain" },
    );

    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "text_content.doc";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const handleLanguage = (event, value) => {
    console.log("e_", event);
    console.log("V_", value);

    if (value) {
      setLanguageError(false);
    }

    if (value !== selectedLanguage) {
      setSecondaryEditorState(EditorState.createEmpty());
      clearListenFunctionality();
    }
    setSelectedLanguage(value);
  };

  const handleDelete = () => {
    setEditorState(EditorState.createEmpty());
    setSecondaryEditorState(EditorState.createEmpty());
    setInputText("");
    setTranslatedText("");
    setDetectedLanguage("");
    setSelectedIndustry("");
    setSelectedProfession("");
    setSelectedLanguage("");
    setWordsCount(0);
    setOpWordsCount(0);
    setWordCheck("");
    setHasText(false);
    setLanguageError(false);
    setHasTranslatedOnce(false);
    clearListenFunctionality();
  };

  const handleInterchange = () => {
    if (!secondaryeditorState.getCurrentContent().hasText()) {
      return;
    }
    const temp = detectedLanguage;
    console.log("temp_", temp);
    setDetectedLanguage(selectedLanguage);
    setSelectedLanguage(temp);

    const tempText = inputText;
    setInputText(translatedText);
    setTranslatedText(tempText);

    const tempEditorState = editorState;
    console.log("editor_", tempEditorState, secondaryeditorState);
    setEditorState(secondaryeditorState);
    setSecondaryEditorState(tempEditorState);

    const firstEditor = secondaryeditorState.getCurrentContent().getPlainText();
    const secondEditor = tempEditorState.getCurrentContent().getPlainText();
    handleWordsCount(firstEditor, "first");
    handleWordsCount(secondEditor, "second");
  };

  const handleWordsCount = (text, type) => {
    const trimmedText = text.trim();
    const words = trimmedText.split(/\s+/).filter((word) => word !== "").length;
    console.log("words_", words);
    if (type == "first") {
      setWordsCount(words);
    } else {
      setOpWordsCount(words);
    }
  };

  return (
    <>
      <SubscriptionRequired
        open={subReqOpen}
        setOpen={setSubReqOpen}
        isFeatureInside={isFeatureInside}
        setIsFeatureInside={setIsFeatureInside}
        path={"translator"}
      />

      {/* Word Limit Warning Dialog */}
      <Dialog
        open={wordLimitDialogOpen}
        onClose={handleWordLimitDialogClose}
        aria-labelledby="word-limit-dialog-title"
        aria-describedby="word-limit-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            padding: 2,
            minWidth: 400,
            background: isDarkMode ? "#1e293b" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
          },
        }}
      >
        <DialogTitle
          id="word-limit-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#ef4444",
            fontWeight: 600,
          }}
        >
          <WarningIcon sx={{ fontSize: 28 }} />
          Word Limit Exceeded
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="word-limit-dialog-description"
            sx={{
              color: isDarkMode ? "#94a3b8" : "#4b5563",
              fontSize: "1rem",
            }}
          >
            You have entered <strong>{wordsCount}</strong> words, which exceeds
            the maximum limit of <strong>1500 words</strong>.
          </DialogContentText>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: isDarkMode ? "#334155" : "#f3f4f6",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? "#e2e8f0" : "#1f2937" }}
            >
              Please reduce your text to 1500 words or less to continue with
              translation.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleWordLimitDialogClose}
            variant="contained"
            sx={{
              bgcolor: "#ef4444",
              "&:hover": {
                bgcolor: "#dc2626",
              },
              borderRadius: 2,
              px: 4,
            }}
          >
            OK, Got it
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          background: isDarkMode
            ? "linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)"
            : "linear-gradient(135deg, #ffffffff 0%, #d3d1d1ff)",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s ease",
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              width: "100%",
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            {/* LEFT SIDE - Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                  },
                }}
              >
                <Breadcrumbs
                  separator={
                    <NavigateNextIcon
                      fontSize="small"
                      sx={{ color: "#667eea" }}
                    />
                  }
                  aria-label="breadcrumb"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        bgcolor: "white",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        mr: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <img
                        alt="logo"
                        src={Poetlogopen}
                        style={{
                          height: "28px",
                          width: "28px",
                          objectFit: "contain",
                          bgcolor: "white",
                          // filter: "drop-shadow(0 0 7px rgba(93, 0, 255, 0.65))",
                          filter:
                            "drop-shadow(0 0 7px rgba(163, 163, 164, 0.65))",
                        }}
                      />
                    </Box>
                    <AutoFixHigh sx={{ fontSize: 20, color: "#667eea" }} />

                    <Link
                      to="/docTags"
                      onClick={(e) => handleBreadcrumbClick(e, "/docTags")}
                      style={{
                        textDecoration: "none",
                        color: "#667eea",
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      MultiTool AI
                    </Link>

                    <NavigateNextIcon
                      fontSize="small"
                      sx={{ color: "#667eea" }}
                    />

                    <Typography
                      sx={{
                        color: "#1a1a1a",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor: "rgba(102, 126, 234, 0.08)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      AI Translator
                    </Typography>
                  </Box>
                </Breadcrumbs>
              </Paper>
            </Box>

            {/* CENTER CONTENT */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                textAlign: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <TranslateIcon
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "primary.main",
                    mr: 2,
                    filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "primary.main",
                    lineHeight: 1.3,
                  }}
                >
                  AI Translator
                </Typography>
              </Box>
            </Box>
          </Box>

          <Zoom in timeout={1000}>
            <Paper
              elevation={4}
              sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: "20px",
                background: isDarkMode
                  ? alpha("#2a2a2a", 0.9)
                  : alpha("#ffffff", 0.95),
                backdropFilter: "blur(15px)",
                flexShrink: 0,
              }}
            >
              <Grid2 container spacing={3} alignItems="center">
                <Grid2 xs={12} sm={6} md={2}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <LanguageIcon
                        sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Detected Language
                      </Typography>
                    </Box>
                    <Chip
                      icon={<LanguageIcon sx={{ fontSize: 16 }} />}
                      label={detectedLanguage || "Auto-detect"}
                      variant={detectedLanguage ? "filled" : "outlined"}
                      color={detectedLanguage ? "primary" : "default"}
                      sx={{
                        minWidth: "150px",
                        fontWeight: 600,
                        height: "40px",
                        fontSize: "0.9rem",
                        borderRadius: "10px",
                        backgroundColor: detectedLanguage
                          ? "primary.main"
                          : isDarkMode
                            ? "black.800"
                            : "black.100",
                        color: detectedLanguage ? "white" : "text.secondary",
                      }}
                    />
                  </Box>
                </Grid2>

                <Grid2 xs={12} sm={6} md={2}>
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.8 }}
                    >
                      <WorkIcon
                        sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Profession
                      </Typography>
                    </Box>
                    <SelectDropdownMui
                      optionsList={professionList}
                      selectLabel="Select..."
                      onChange={(e) => setSelectedProfession(e.target.value)}
                      value={selectedProfession}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          height: "40px",
                          minWidth: "160px",
                          "& .MuiSelect-select": {
                            color: selectedProfession
                              ? "text.primary"
                              : "text.disabled",
                            fontWeight: selectedProfession ? 500 : 400,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "text.disabled",
                        },
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 xs={12} sm={6} md={2}>
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.8 }}
                    >
                      <BusinessIcon
                        sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Industry
                      </Typography>
                    </Box>
                    <SelectDropdownMui
                      optionsList={industryList}
                      selectLabel="Select..."
                      onChange={(e) => setSelectedIndustry(e.target.value)}
                      value={selectedIndustry}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          height: "40px",
                          minWidth: "160px",
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                            paddingRight: "32px !important",
                            color: selectedIndustry
                              ? "text.primary"
                              : "text.disabled",
                            fontWeight: selectedIndustry ? 500 : 400,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "text.disabled",
                        },
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 xs={12} sm={6} md={2}>
                  <Box id="translate-to-section">
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.8 }}
                    >
                      <TranslateIcon
                        sx={{
                          fontSize: 18,
                          color: languageError ? "#f44336" : "text.secondary",
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: languageError ? "#f44336" : "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        Translate To
                        <span
                          style={{
                            color: "#f44336",
                            marginLeft: "4px",
                            fontWeight: "bold",
                          }}
                        >
                          *
                        </span>
                      </Typography>
                    </Box>
                    <SelectDropdownSearch
                      optionsList={languageList}
                      selectLabel="Select..."
                      onChange={handleLanguage}
                      value={selectedLanguage}
                      isDarkMode={isDarkMode}
                      error={languageError}
                      inputRef={languageSelectRef}
                    />
                    {languageError && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#f44336",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          display: "block",
                          fontWeight: 500,
                        }}
                      >
                        Please select a language to translate to
                      </Typography>
                    )}
                  </Box>
                </Grid2>
                <Grid2 xs={12} sm={6} md={1.5}>
                  <Box
                    sx={{ textAlign: "center", mt: { xs: 2, sm: 3 }, mb: 0.8 }}
                  >
                    <LightTooltip title="Clear All Content">
                      <Button
                        onClick={handleDelete}
                        variant="outlined"
                        color="error"
                        startIcon={<ClearIcon />}
                        disabled={!hasText}
                        sx={{
                          mt: 0.4,
                          borderRadius: "10px",
                          height: "40px",
                          minWidth: "120px",
                          textTransform: "none",
                          fontWeight: 600,
                          borderWidth: "2px",
                          "&:hover": {
                            borderWidth: "2px",
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.04,
                            ),
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 8px rgba(244, 67, 54, 0.2)",
                          },
                          transition: "all 0.2s ease",
                          "&:disabled": {
                            borderColor: "black.300",
                            color: "black.500",
                          },
                        }}
                      >
                        Clear All
                      </Button>
                    </LightTooltip>
                  </Box>
                </Grid2>
              </Grid2>
            </Paper>
          </Zoom>

          <Loader loading={loading} />

          {/* Main content area - takes remaining height */}
          <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
            <Grid2 container spacing={3} sx={{ height: "100%" }}>
              <Grid2 xs={12} md={6} sx={{ height: "100%" }}>
                <Fade in timeout={1400}>
                  <Card
                    elevation={8}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      overflow: "hidden",
                      background: isDarkMode
                        ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
                        : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          background:
                            "linear-gradient(90deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))",
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Circle sx={{ fontSize: 12, mr: 1 }} />
                              Source Text
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <LightTooltip title="Swap Languages" placement="top">
                              <IconButton
                                onClick={handleInterchange}
                                disabled={
                                  !secondaryeditorState
                                    .getCurrentContent()
                                    .hasText()
                                }
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                  color: "white",
                                  width: 38,
                                  height: 37,
                                  borderRadius: "20px",
                                  boxShadow:
                                    "0 4px 12px rgba(102, 126, 234, 0.3)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                                    transform: "translateY(-2px)",
                                    boxShadow:
                                      "0 6px 16px rgba(102, 126, 234, 0.4)",
                                  },
                                  "&:active": {
                                    transform: "translateY(0)",
                                  },
                                  "&:disabled": {
                                    background: "black.300",
                                    color: "black.500",
                                    boxShadow: "none",
                                    transform: "none",
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                <SwapHorizIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </LightTooltip>
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          p: 2,
                          "& .DraftEditor-root": {
                            height: "100%",
                            overflow: "auto",
                            fontSize: "16px",
                            lineHeight: 1.6,
                            color: isDarkMode ? "white" : "text.primary",
                          },
                          "& .public-DraftEditor-content": {
                            height: "100%",
                          },
                          "& .public-DraftEditorPlaceholder-root": {
                            color: "text.secondary",
                            fontSize: "16px",
                          },
                        }}
                      >
                        <Editor
                          editorState={editorState}
                          onChange={handleEditorChange}
                          placeholder="Enter your text to translate..."
                        />
                      </Box>
                      <Box
                        sx={{
                          p: 1,
                          borderTop: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          background: alpha(
                            theme.palette.background.paper,
                            0.5,
                          ),
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Chip
                          icon={<Circle sx={{ fontSize: "12px !important" }} />}
                          label={`${wordsCount}/1500 Words`}
                          variant="outlined"
                          size="small"
                          color={wordsCount > 1400 ? "warning" : "primary"}
                          sx={{
                            bgcolor: wordsCount > 1500 ? "#ef4444" : "inherit",
                            color: wordsCount > 1500 ? "white" : "inherit",
                            "& .MuiChip-label": {
                              color: wordsCount > 1500 ? "white" : "inherit",
                            },
                          }}
                        />

                        <Button
                          variant="contained"
                          onClick={() => handleTranslate(true)}
                          disabled={
                            !hasText ||
                            !selectedLanguage ||
                            !accessToTranslate ||
                            loading
                          }
                          startIcon={<TranslateIcon />}
                          sx={{
                            borderRadius: "12px",
                            px: 3,
                            py: 1,
                            background:
                              wordsCount > 1500
                                ? "#ef4444"
                                : "linear-gradient(45deg, #75dab1ff, #38885faf)",
                            "&:hover": {
                              background:
                                wordsCount > 1500
                                  ? "#dc2626"
                                  : "linear-gradient(45deg, #5a6fd8, #6a4190)",
                              transform: "translateY(-2px)",
                              boxShadow:
                                wordsCount > 1500
                                  ? "0 8px 25px rgba(239,68,68,0.4)"
                                  : "0 8px 25px rgba(102,126,234,0.4)",
                            },
                            "&:disabled": {
                              background: "black.300",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {loading ? "Translating..." : "Translate"}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid2>
              <Grid2 xs={12} md={6} sx={{ height: "100%" }}>
                <Fade in timeout={1400}>
                  <Card
                    elevation={8}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      overflow: "hidden",
                      background: isDarkMode
                        ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
                        : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                      backdropFilter: "blur(10px)",
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          background:
                            "linear-gradient(90deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))",
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "secondary.main",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Circle sx={{ fontSize: 12, mr: 1 }} />
                          Translation
                          {selectedLanguage && (
                            <Chip
                              label={selectedLanguage}
                              size="small"
                              sx={{ ml: 2, fontWeight: 600 }}
                            />
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          p: 2,
                          "& .DraftEditor-root": {
                            height: "100%",
                            overflow: "auto",
                            fontSize: "16px",
                            lineHeight: 1.6,
                            color: isDarkMode ? "white" : "text.primary",
                          },
                          "& .public-DraftEditor-content": {
                            height: "100%",
                          },
                        }}
                      >
                        <Editor
                          editorState={secondaryeditorState}
                          onChange={handleOuputChange}
                          readOnly={true}
                          placeholder="Translation will appear here..."
                        />
                      </Box>
                      <Box
                        sx={{
                          p: 1,
                          borderTop: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1,
                          )}`,
                          background: alpha(
                            theme.palette.background.paper,
                            0.5,
                          ),
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Chip
                          icon={<Circle sx={{ fontSize: "12px !important" }} />}
                          label={`${opWordsCount}/1500 Words`}
                          variant="outlined"
                          size="small"
                          color="secondary"
                        />

                        <Box
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <LightTooltip title="Download">
                            <IconButton
                              onClick={handleDownload}
                              disabled={!opWordsCount}
                              sx={{
                                color: "success.main",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.success.main,
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </LightTooltip>

                          <LightTooltip title="Copy">
                            <IconButton
                              onClick={handlecopy}
                              disabled={!opWordsCount}
                              sx={{
                                color: "info.main",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </LightTooltip>

                          <LightTooltip title="Listen">
                            <IconButton
                              onClick={handleListen}
                              disabled={!opWordsCount}
                              sx={{
                                color: "warning.main",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.warning.main,
                                    0.1,
                                  ),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <VolumeUp />
                            </IconButton>
                          </LightTooltip>

                          {showPauseResumeToggle && (
                            <>
                              <LightTooltip title={isPlaying ? "Pause" : "Resume"}>
                                <IconButton
                                  onClick={handleTogglePlay}
                                  sx={{ color: "text.secondary" }}
                                >
                                  {isPlaying ? <Pause /> : <PlayArrow />}
                                </IconButton>
                              </LightTooltip>

                              <Box sx={{ width: 100, ml: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <SpeedIcon
                                    sx={{
                                      fontSize: 16,
                                      color: "text.secondary",
                                    }}
                                  />
                                  <Slider
                                    value={speed}
                                    min={0.25}
                                    max={2}
                                    step={0.25}
                                    onChange={handleSpeedChange}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${value}x`}
                                    size="small"
                                    sx={{
                                      "& .MuiSlider-thumb": {
                                        width: 16,
                                        height: 16,
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid2>
            </Grid2>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ModernTranslator;
